import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';

/**
 * A button that acts only once it has been held down for `holdMs` (#632, I1,
 * F3): the circle fills to 360° while it is held, and letting go early does
 * nothing, as does losing focus or the window. For the commands that start
 * motors, where a stray click must not be enough. The keyboard holds it too,
 * with Space or Enter.
 */
interface HoldToConfirmButtonProps {
  label: string;
  onConfirm: () => void;
  disabled?: boolean;
  holdMs?: number;
}

const TICK_MS = 50;

export const HoldToConfirmButton: React.FC<HoldToConfirmButtonProps> = ({
  label,
  onConfirm,
  disabled = false,
  holdMs = 1500,
}) => {
  const [progress, setProgress] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAt = useRef(0);

  const release = () => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
    setProgress(0);
  };

  const press = () => {
    if (disabled || timer.current) return;
    startedAt.current = Date.now();
    timer.current = setInterval(() => {
      const held = Date.now() - startedAt.current;
      if (held >= holdMs) {
        release();
        onConfirm();
        return;
      }
      setProgress((held / holdMs) * 100);
    }, TICK_MS);
  };

  useEffect(() => release, []);

  const isHoldKey = (key: string) => key === ' ' || key === 'Enter';

  return (
    <Button
      variant="contained"
      color="warning"
      disabled={disabled}
      onPointerDown={press}
      onPointerUp={release}
      onPointerLeave={release}
      onPointerCancel={release}
      // Focus or the window lost mid-hold is not a hold kept.
      onBlur={release}
      onKeyDown={(e) => {
        if (isHoldKey(e.key) && !e.repeat) {
          e.preventDefault();
          press();
        }
      }}
      onKeyUp={(e) => {
        if (isHoldKey(e.key)) release();
      }}
      // A click alone never confirms: the hold does.
      onClick={(e) => e.preventDefault()}
      aria-label={`${label} (press and hold)`}
      startIcon={
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={progress}
            size={20}
            color="inherit"
            aria-label="hold progress"
          />
        </Box>
      }
    >
      {label}
    </Button>
  );
};
